import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { NgModule, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ServiceWorkerModule } from '@angular/service-worker';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { ModalModule } from 'ngx-bootstrap/modal';
import { NgPipesModule } from 'ngx-pipes';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { ChangePasswordComponent } from './base/components/change-password/change-password.component';
import { ForgotPasswordComponent } from './base/components/forgot-password/forgot-password.component';
import { LoginComponent } from './base/components/login/login.component';
import { NotfoundComponent } from './base/components/notfound/notfound.component';
import { ResetPasswordComponent } from './base/components/reset-password/reset-password.component';
import { AdminDashboardComponent } from './features/admin/components/admin-dashboard/admin-dashboard.component';
import { RenameClientDialogComponent } from './features/admin/components/admin-dashboard/rename-client-dialog/rename-client-dialog.component';
import { DeleteClientDialogComponent } from './features/admin/components/admin-dashboard/delete-client-dialog/delete-client-dialog.component';
import { UpdateUserComponent } from './features/admin/components/user/update-user/update-user.component';
import { UserComponent } from './features/admin/components/user/user.component';
import { DemoRequestComponent } from './features/public/components/demo-request/demo-request.component';
import { DemoComponent } from './features/public/components/demo/demo.component';
import { HomeComponent } from './features/public/components/home/home.component';
import { AccountComponent } from './features/user/components/account/account.component';
import { AccountDetailsComponent } from './features/user/components/account/components/account-details/account-details.component';
import { AccountFilesComponent } from './features/user/components/account/components/account-files/account-files.component';
import { BomTableComponent } from './features/user/components/bom-table/bom-table.component';
import { ExportListComponent } from './features/user/components/bom-table/components/export-list/export-list.component';
import { RegionNavigationComponent } from './features/user/components/region-navigation/region-navigation.component';
import { UserDashboardComponent } from './features/user/components/user-dashboard/user-dashboard.component';
import { View3dComponent } from './features/user/components/view3d/view3d.component';
import { View3dDialogComponent } from './features/user/dialogs/view3d-dialog/view3d-dialog.component';
import { AppLoadingOverlay } from './shared/components/app-loading-overlay/app-loading-overlay.component';
import { AppNotifications } from './shared/components/app-notifications/app-notifications.component';
import { BreadcrumbNavigationComponent } from './shared/components/breadcrumb-navigation/breadcrumb-navigation.component';
import { ButtonScrollTabsComponent } from './shared/components/button-scroll-tabs/button-scroll-tabs.component';
import { CardComponent } from './shared/components/card/card.component';
import { ChatComponent } from './shared/components/chat/chat.component';
import { ChatMessageComponent } from './shared/components/chat/components/chat-message/chat-message.component';
import { ConfigFieldComponent } from './shared/components/config-field/config-field.component';
import { DocumentUnavailableDialogComponent } from './shared/components/document-unavailable-dialog/document-unavailable-dialog.component';
import { DropFileComponent } from './shared/components/drop-file/drop-file.component';
import { MenuModule } from './shared/components/menu/menu.module';
import { NavBarComponent } from './shared/components/nav-bar/nav-bar.component';
import { SideBarComponent } from './shared/components/nav-sidebar/sidebar.component';
import { PasswordFieldComponent } from './shared/components/password-field/password-field.component';
import { PopoverChatComponent } from './shared/components/popover-chat/popover-chat.component';
import { BomTableComparisonModule } from './features/user/components/bom-table-comparison/bom-table-comparison.module';
import {
  SelectionListComponent,
  SelectionListItemDirective,
  SelectionSelectAllDirective,
} from './shared/components/selection-list/selection-list.component';
import { UploadFileComponent } from './shared/components/upload-file/upload-file.component';
import { AutosizeDirective } from './shared/directives/autosize.directive';
import { DropFileDirective } from './shared/directives/drop-file.directive';
import { FocusDirective } from './shared/directives/focus.directive';
import { HttpRequestInterceptor } from './shared/httpRequestInterceptor';
import { FileSizePipe } from './shared/pipes/file-size.pipe';
import { MatTreeModule } from '@angular/material/tree';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { SelectionService } from './shared/services/selection.service';
import { ContactUsComponent } from './base/components/contact-us/contact-us.component';
import { UserProfileAvatarModule } from './shared/components/user-profile-avatar/user-profile-avatar.module';
import { BgRecentVisitsService } from './shared/services/bg-recent-visits.service';
import { UserModule } from './features/user/user.module';
import { SessionTimeoutDialogComponent } from './shared/components/session-timeout-dialog/session-timeout-dialog.component';
import { ConfirmationDialogComponent } from './shared/components/confirmation-dialog/confirmation-dialog.component';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatListModule } from '@angular/material/list';
import { MarkdownModule } from 'ngx-markdown';
import { TestLoadingComponent } from './features/test-loading/test-loading.component';
import { environment } from '../environments/environment';
import { PushNotificationManagerComponent } from './shared/components/push-notification-manager/push-notification-manager.component';
import { CommunicationPanelComponent } from './features/admin/components/communication-panel/communication-panel.component';
import { DrafterMessageComponent } from './features/user/components/topic/drafter-message/drafter-message.component';
import { ProjectListComponent } from './features/user/components/topic/project-list/project-list.component';
import { TopicSidebarComponent } from './features/user/components/topic/topic-sidebar/topic-sidebar.component';
import { QuillModule } from 'ngx-quill';
import { DocumentationComponent } from './features/user/components/documentation-component/documentation.component';
import { TurnstileComponent } from './shared/components/turnstile/turnstile.component';

@NgModule({
  declarations: [
    AppComponent,
    HomeComponent,
    NotfoundComponent,
    UserDashboardComponent,
    AdminDashboardComponent,
    RenameClientDialogComponent,
    DeleteClientDialogComponent,
    LoginComponent,
    ForgotPasswordComponent,
    ResetPasswordComponent,
    ChangePasswordComponent,
    AccountComponent,
    UserComponent,
    ConfigFieldComponent,
    // ClientComponent,
    // CreateUserComponent,
    UpdateUserComponent,
    // CreateClientComponent,
    // UpdateClientComponent,
    // DeleteUserComponent,
    // UploadFileComponent,
    // UploadFileListComponent,
    BomTableComponent,
    RegionNavigationComponent,
    NavBarComponent,
    SideBarComponent,
    BreadcrumbNavigationComponent,
    View3dDialogComponent,
    PasswordFieldComponent,
    ButtonScrollTabsComponent,
    CardComponent,
    DropFileComponent,
    DropFileDirective,
    UploadFileComponent,
    FileSizePipe,
    AccountDetailsComponent,
    AccountFilesComponent,
    SelectionListComponent,
    SelectionListItemDirective,
    SelectionSelectAllDirective,
    ExportListComponent,
    View3dComponent,
    ChatComponent,
    ChatMessageComponent,
    AutosizeDirective,
    FocusDirective,
    PopoverChatComponent,
    DemoRequestComponent,
    DemoComponent,
    AppNotifications,
    AppLoadingOverlay,
    DocumentUnavailableDialogComponent,
    SessionTimeoutDialogComponent,
    ConfirmationDialogComponent,
    PushNotificationManagerComponent,
    ContactUsComponent,
    TestLoadingComponent,
    CommunicationPanelComponent,
    DrafterMessageComponent,
    ProjectListComponent,
    TopicSidebarComponent,
    DocumentationComponent,
    TurnstileComponent,
  ],
  imports: [
    CommonModule,
    ModalModule.forRoot(),
    MarkdownModule.forRoot(),
    BrowserModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    HttpClientModule,
    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled: environment.production, // Enable in development for testing
      registrationStrategy: 'registerWhenStable:30000'
    }),
    NgbModule,
    FormsModule,
    ReactiveFormsModule,
    NgPipesModule,
    QuillModule.forRoot(),
    MatTreeModule,
    MatCheckboxModule,
    MatButtonModule,
    MatCardModule,
    MatGridListModule,
    MatListModule,
    MatIconModule,
    MatSnackBarModule,
    MenuModule,
    UserProfileAvatarModule,
    UserModule,
    BomTableComparisonModule,
  ],
  providers: [
    {
      provide: HTTP_INTERCEPTORS, useClass: HttpRequestInterceptor, multi: true
    },
    BgRecentVisitsService
  ],
  bootstrap: [AppComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class AppModule { }
